import React, { type FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';
import { SearchIcon, XIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import type { SelectOption } from '@/components/ui/select';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { FilterButton } from '@/features/estates/components/filter-button';
import { ViewSwitch } from '@/features/estates/components/view-switch';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { cn } from '@/lib/utils';

import { ESTATES_TOOLBAR_ROUTES, type EstatesToolbarUrl } from '../constants/routes';
import { EstateFilterDrawer } from './estate-filter-drawer';

type EstateFilters = {
  searchString?: string;
  propertyType?: 'house' | 'apartment';
  marketType?: 'buy' | 'rent' | 'sell';
  usageType?: 'commercial';
  propertyLevel?: string;
};

export interface ToolbarProps {
  url: EstatesToolbarUrl;
  showViewSwitch?: boolean;
  creatorList?: SelectOption[];
  rightButtonAction?: React.ReactNode;
  searchString?: string;
  onSearchStringChange?: (value: string) => void;
}

type FilterChipProps = {
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick: () => void;
};

const FilterChip: FC<FilterChipProps> = ({ active, disabled, children, onClick }) => {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      aria-disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 rounded-full border px-5 py-1 text-sm font-medium transition-colors',
        disabled
          ? 'cursor-not-allowed border-[hsl(214.3_31.8%_91.4%)] bg-[#f8f9fa] text-slate-400'
          : active
            ? 'border-[#07B4C0] bg-[#D9F3F7] text-[#2F3A4A] hover:border-[#07B4C0] hover:bg-[#D9F3F7]'
            : 'border-[hsl(214.3_31.8%_91.4%)] bg-[#f8f9fa] text-[#68778E] hover:border-[#07B4C0] hover:bg-[#D9F3F7]'
      )}
    >
      <span>{children}</span>
      {active && !disabled ? <XIcon className="h-3 w-3" /> : null}
    </button>
  );
};

export const Toolbar = ({
  url,
  creatorList,
  showViewSwitch = true,
  rightButtonAction,
  onSearchStringChange,
}: ToolbarProps) => {
  const { t } = useTranslation();
  const route = getRouteApi(url);

  const search = route.useSearch();
  const location = route.useMatch();
  const navigate = route.useNavigate();

  const isMainEstatesPage = url === ESTATES_TOOLBAR_ROUTES.ESTATES;

  const filters = useMemo<EstateFilters>(() => {
    return (search.filters ?? {}) as EstateFilters;
  }, [search.filters]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState(() => {
    const value = filters.searchString;
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  });

  useEffect(() => {
    if (!isMainEstatesPage) return;

    const input = inputRef.current;
    if (!input) return;

    if (document.activeElement !== input) {
      input.focus();
      const length = input.value.length;
      input.setSelectionRange?.(length, length);
    }
  }, [isMainEstatesPage]);

  const updateFilters = useCallback(
    (updater: (filters: EstateFilters, prev: typeof search) => EstateFilters) => {
      navigate({
        search: (prev) => {
          const prevFilters = (prev.filters ?? {}) as EstateFilters;
          const nextFilters = updater({ ...prevFilters }, prev);

          const prevKeys = Object.keys(prevFilters);
          const nextKeys = Object.keys(nextFilters);

          const hasSameLength = prevKeys.length === nextKeys.length;
          const hasSameValues =
            hasSameLength &&
            prevKeys.every(
              (key) =>
                prevFilters[key as keyof EstateFilters] === nextFilters[key as keyof EstateFilters]
            );

          if (hasSameValues) {
            return prev;
          }

          return {
            ...prev,
            filters: nextFilters,
          };
        },
      });
    },
    [navigate]
  );

  const debouncedUpdateSearch = useDebouncedCallback((value: string) => {
    if (onSearchStringChange) {
      onSearchStringChange(value);
    } else {
      updateFilters((filters) => {
        if (value.trim().length) {
          filters.searchString = value;
        } else {
          delete filters.searchString;
        }
        return filters;
      });
    }
  }, 1000);
  const handleClearSearch = useCallback(() => {
    setInputValue('');
    if (onSearchStringChange) {
      onSearchStringChange('');
    } else {
      updateFilters((filters) => {
        delete filters.searchString;
        return filters;
      });
    }
    const input = inputRef.current;
    if (input) {
      input.focus();
    }
  }, [onSearchStringChange, updateFilters]);

  const toggleFilter = useCallback(
    (key: 'propertyType' | 'marketType' | 'usageType', value: string) => {
      updateFilters((filters) => {
        if (filters[key] === value) {
          delete filters[key];
        } else {
          filters[key] = value as never;
        }
        return filters;
      });
    },
    [updateFilters]
  );

  const toggleEstates = useCallback(() => {
    navigate({
      search: (prev) => {
        const prevFilters = (prev.filters ?? {}) as EstateFilters;
        const isActive =
          'filterOwn' in prev &&
          !prev.filterOwn &&
          prevFilters.propertyLevel === PROPERTY_LEVELS.OBJECT;

        if (isActive) {
          const { propertyLevel: _ignored, ...restFilters } = prevFilters;
          return {
            ...prev,
            filters: restFilters,
          };
        }

        return {
          ...prev,
          filterOwn: false,
          filters: {
            ...prevFilters,
            propertyLevel: PROPERTY_LEVELS.OBJECT,
          },
        };
      },
    });
  }, [navigate]);

  const toggleMyEstates = useCallback(() => {
    navigate({
      search: (prev) => {
        const prevFilters = (prev.filters ?? {}) as EstateFilters;
        const isActive =
          'filterOwn' in prev &&
          prev.filterOwn &&
          prevFilters.propertyLevel === PROPERTY_LEVELS.OBJECT;

        if (isActive) {
          const { propertyLevel: _ignored, ...restFilters } = prevFilters;
          return {
            ...prev,
            filterOwn: false,
            filters: restFilters,
          };
        }

        return {
          ...prev,
          filterOwn: true,
          filters: {
            ...prevFilters,
            propertyLevel: PROPERTY_LEVELS.OBJECT,
          },
        };
      },
    });
  }, [navigate]);

  const toggleProjects = useCallback(() => {
    updateFilters((filters) => {
      const isActive = filters.propertyLevel === PROPERTY_LEVELS.PROJECT;

      if (isActive) {
        delete filters.propertyLevel;
        return filters;
      }

      return {
        ...filters,
        propertyLevel: PROPERTY_LEVELS.PROJECT,
        propertyType: undefined,
        usageType: undefined,
        marketType: undefined,
      };
    });
  }, [updateFilters]);

  const { propertyType, marketType, usageType, propertyLevel } = filters;

  const isEstatesActive =
    'filterOwn' in search && !search.filterOwn && propertyLevel === PROPERTY_LEVELS.OBJECT;
  const isMyEstatesActive =
    'filterOwn' in search && search.filterOwn && propertyLevel === PROPERTY_LEVELS.OBJECT;

  const isProjectsActive = propertyLevel === PROPERTY_LEVELS.PROJECT;

  if (!isMainEstatesPage) {
    return (
      <>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FilterButton url={url} />
            {showViewSwitch && <ViewSwitch url={url} from={location.pathname} />}
          </div>
          {rightButtonAction}
        </div>
        <EstateFilterDrawer url={url} creatorList={creatorList} />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Input
            ref={inputRef}
            size="sm"
            wrapperClassName="flex-1"
            className="h-[46px] !rounded-[6px] border !border-border bg-white text-text-secondary shadow-[0_1px_2px_rgba(16,24,40,0.04)] placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light focus-visible:outline-none"
            startIcon={<SearchIcon className="h-4 w-4 text-text-secondary" />}
            autoFocus
            placeholder={t(
              'search-estates-placeholder',
              'Search estates by title, address, price, area, rooms, creator, or date...'
            )}
            value={inputValue}
            endIcon={
              inputValue ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleClearSearch();
                  }}
                  aria-label={t('clearSearch', 'Clear search')}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-primary hover:bg-primary-lighter/20"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              ) : null
            }
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
              debouncedUpdateSearch(value);
            }}
          />
          <FilterButton url={url} />
          {showViewSwitch ? <ViewSwitch url={url} from={location.pathname} /> : null}
          {rightButtonAction}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <FilterChip active={isEstatesActive} onClick={toggleEstates}>
              {t('estates')}
            </FilterChip>

            <FilterChip active={isMyEstatesActive} onClick={toggleMyEstates}>
              {t('myEstates')}
            </FilterChip>

            <FilterChip active={isProjectsActive} onClick={toggleProjects}>
              {t('projects')}
            </FilterChip>

            <span className="mx-1 h-5 w-px bg-slate-400" />

            <FilterChip
              active={propertyType === 'house'}
              disabled={isProjectsActive}
              onClick={() => toggleFilter('propertyType', 'house')}
            >
              {t('house')}
            </FilterChip>

            <FilterChip
              active={propertyType === 'apartment'}
              disabled={isProjectsActive}
              onClick={() => toggleFilter('propertyType', 'apartment')}
            >
              {t('apartment')}
            </FilterChip>

            <FilterChip
              active={usageType === 'commercial'}
              disabled={isProjectsActive}
              onClick={() => toggleFilter('usageType', 'commercial')}
            >
              {t('commercial')}
            </FilterChip>

            <FilterChip
              active={marketType === 'buy'}
              disabled={isProjectsActive}
              onClick={() => toggleFilter('marketType', 'buy')}
            >
              {t('purchase')}
            </FilterChip>

            <FilterChip
              active={marketType === 'rent'}
              disabled={isProjectsActive}
              onClick={() => toggleFilter('marketType', 'rent')}
            >
              {t('rent')}
            </FilterChip>
          </div>
        </div>
      </div>

      <EstateFilterDrawer url={url} creatorList={creatorList} />
    </>
  );
};
